import SiteLayout from "../components/SiteLayout";
import SEO from "../components/SEO";
import CTABanner from "../components/CTABanner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { LifeBuoy } from "lucide-react";
import { Link } from "react-router-dom";

const faqs = [
 {
 q: "Is Golden Life Standard Health a telehealth-only practice?",
 a: "Yes. Every appointment is delivered virtually so you can begin care from a private space, your home, parked car, or quiet office, without the friction of travel. The clinical depth is identical to in-person psychiatry: the same intake, the same assessments, the same medication decisions, the same follow-up cadence. Most of our patients tell us it actually feels more personal because there is no waiting-room stress and no rushed hand-off, just protected time with the clinician.",
 },
 {
 q: "Who do you treat?",
 a: "Adolescents, young adults, working adults, and older adults across mood disorders (depression, bipolar spectrum), anxiety disorders (generalized anxiety, panic, social anxiety, OCD-adjacent presentations), ADHD, trauma-related conditions, sleep concerns, perinatal mental health, and substance use challenges. We regularly co-manage complex cases with therapists, primary care physicians, and OB-GYNs. If our team is not the best fit for your situation, we will connect you with a colleague or specialty program who is, no discharge into the void.",
 },
 {
 q: "How fast can I be seen?",
 a: "Most patients are booked into a first evaluation within 5-7 days of reaching out. Evening slots (until 8:00 PM ET) and Saturday appointments are available. If you are experiencing an urgent but non-emergency change (worsening symptoms, medication access issue, etc.) let our care coordinator know when you book and we will do our best to expedite.",
 },
 {
 q: "Do you take insurance?",
 a: "Yes, we accept most major commercial plans including Aetna, Blue Cross Blue Shield, Cigna, United Healthcare, and Optum, plus Medicare and Maryland Medicaid. Because plan networks change quarterly, we always run a real-time benefits check before your first visit so you know your copay, coinsurance, and deductible position exactly. Coverage can be verified in under a minute on our Insurance page.",
 },
 {
 q: "What if I do not have insurance?",
 a: "We offer transparent self-pay rates that are collected securely through Stripe at booking. Standard rates are $180 for a new-patient psychiatric evaluation (60 minutes) and $95 for a follow-up medication visit (30 minutes). If cost is a barrier, please reach out; we hold a limited number of sliding-scale slots each month for patients who need them.",
 },
 {
 q: "Do you prescribe medication?",
 a: "Yes. Our board-certified psychiatric clinicians (MD and psychiatric mental-health nurse practitioners) prescribe and manage the full range of psychotropic medications when clinically appropriate. We take shared-decision-making seriously: we will walk through the evidence, alternatives, expected timeline, common side effects, and monitoring plan before any prescription is sent to your pharmacy. Nothing gets rubber-stamped.",
 },
 {
 q: "Do you prescribe controlled substances?",
 a: "We follow current DEA guidance and Maryland state regulations. Certain controlled medications (for example, some ADHD stimulants and benzodiazepines) can be initiated or refilled via telehealth under specific conditions, while others may require additional steps such as an in-person visit with a partner clinician. Your clinician will explain what applies to your specific situation during the evaluation and will never leave you guessing about a refill plan.",
 },
 {
 q: "What technology do I need for a visit?",
 a: "Any device with a working camera, microphone, and a stable internet connection: a phone, tablet, Chromebook, or laptop all work. There is no app to download. About an hour before your visit we email you a secure, one-click link. Click it, allow camera and microphone access when your browser asks, and you are in. If technology is a challenge we can also do phone-only visits when clinically appropriate.",
 },
 {
 q: "Is my information confidential?",
 a: "Absolutely. Our video, messaging, and file uploads all run on HIPAA-aligned infrastructure with encryption in transit and at rest. Only members of your direct care team can see your records, and every access is logged. See our Privacy & Security page for the full technical detail. We do not sell data. We do not train AI on your notes. We do not use your visit for marketing.",
 },
 {
 q: "What documents do I need for my first appointment?",
 a: "A government-issued photo ID, your insurance or Medicaid card (if applicable), and a list of any current medications with dosages. If you have recent lab work, prior psychiatric records, or a therapist's letter of referral, upload those too, they make the first visit far more efficient. Everything can be uploaded securely at booking, no email attachments or scanning required.",
 },
 {
 q: "How long is a first appointment vs. a follow-up?",
 a: "A new-patient evaluation is 60 minutes. That length lets us take a proper history, discuss goals, review any prior treatments, and build a plan you actually agree with, rather than a rushed 15-minute check-box exercise. Follow-up medication visits are 30 minutes. If you need more time for a follow-up (life change, medication change, therapy coordination) just tell the coordinator and we will schedule a double slot.",
 },
 {
 q: "Do I need to live in Maryland?",
 a: "Yes. Our clinicians are licensed to practice in Maryland and, per state regulations, you must be physically located in Maryland at the moment of each visit. This applies even to a brief travel day. If you split time between states, tell us at booking and we will map out a coverage plan (either coordinating with a clinician in your other state, or pausing until you are back).",
 },
 {
 q: "Do you offer therapy?",
 a: "We are a psychiatry practice, so our clinicians provide medication-focused care with supportive counseling during each visit. For weekly talk therapy (CBT, DBT, EMDR, couples work, etc.), we partner with a curated network of Maryland-licensed therapists and will provide a warm introduction, no cold Google searches needed.",
 },
 {
 q: "Can I change or cancel my appointment?",
 a: "Yes. Reach our care coordinator at care@goldenlifesh.com or (443) 891-4248 at least 24 hours before your visit and we will reschedule with no fee. Late cancellations or no-shows may be assessed a fee (disclosed at booking) so we can keep same-week openings available for other patients.",
 },
 {
 q: "How do refills work between visits?",
 a: "During your visit your clinician will send prescriptions directly to your pharmacy of choice, usually with enough refills to bridge until your next scheduled follow-up. If something changes (side effect, dose adjustment, pharmacy switch) send a secure message and our team responds within one business day.",
 },
 {
 q: "What if I am in crisis?",
 a: "If you or a loved one are in immediate danger, please call 988 (Suicide & Crisis Lifeline) or 911 right away. This website and our clinical inbox are not designed for real-time emergency response. Once you are safe, we are happy to be part of the follow-up care plan and will make time to see you as soon as possible.",
 },
];

const INTRO_COPY = "Below are the questions we hear most from patients considering online psychiatric care in Maryland. If your question is not covered, our care coordinator is one email or phone call away and will respond the same business day. We know choosing a psychiatric practice is a personal decision, and we would rather over-explain than leave you with unanswered questions before your first visit.";
const CLOSING_COPY = "Still deciding whether Golden Life Standard Health is the right fit? A short intake call with our care coordinator is complimentary and there is zero pressure to book after. We will listen, share what our clinicians can and cannot offer, and help you weigh whether telepsychiatry is a fit for your season of life.";

export default function FAQ() {
 const faqLd = {
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: faqs.map((f) => ({
 "@type": "Question",
 name: f.q,
 acceptedAnswer: { "@type": "Answer", text: f.a },
 })),
 };
 return (
 <SiteLayout>
 <SEO
 title="Frequently Asked Questions"
 description="Answers to common questions about online psychiatry: insurance, medications, telehealth privacy, emergencies, and how to prepare for your first visit."
 path="/faq"
 />
 <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} /> <section data-testid="faq-hero" className="container-pad max-w-7xl mx-auto pt-14 lg:pt-20 pb-12">
 <div className="grid lg:grid-cols-12 gap-12 items-center">
 <div className="lg:col-span-7">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Frequently asked</p>
 <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink text-balance">
 Answers to <span className="italic text-sage-600">the questions we hear most.</span>
 </h1>
 <p className="mt-6 text-lg text-muted_text leading-relaxed max-w-2xl">
 Don&apos;t see what you&apos;re looking for? <Link to="/contact" className="text-sage-700 hover:text-gold-600 font-medium">Send us a message</Link>, a real person will reply within one business day.
 </p>
 </div>
 <div className="lg:col-span-5">
 <div className="relative">
 <img
 src="https://images.pexels.com/photos/4144096/pexels-photo-4144096.jpeg?auto=compress&cs=tinysrgb&w=1200"
 alt="A person thoughtfully reading answers on a tablet"
 className="rounded-3xl w-full h-[420px] object-cover shadow-[0_30px_60px_-30px_rgba(26,43,51,0.3)]"
 loading="lazy"
 />
 <div className="absolute -top-6 -left-6 w-32 h-32 rounded-3xl bg-gold-500/15 -z-10" />
 <div className="absolute -bottom-8 -right-6 w-40 h-40 rounded-3xl bg-sage-500/15 -z-10" />
 </div>
 </div>
 </div>
 </section>

 <section data-testid="faq-list" className="container-pad max-w-4xl mx-auto pb-20">
 <p data-testid="faq-intro" className="text-muted_text leading-relaxed mb-8 max-w-3xl text-[15px]">
 {INTRO_COPY}
 </p>
 <Accordion type="single" collapsible className="space-y-3">
 {faqs.map((f, i) => (
 <AccordionItem key={i} value={`q-${i}`} data-testid={`faq-item-${i}`} className="card-soft px-6 sm:px-7 border-none">
 <AccordionTrigger className="text-left font-heading text-lg font-semibold text-ink hover:no-underline py-5">
 {f.q}
 </AccordionTrigger>
 <AccordionContent className="text-muted_text leading-relaxed pb-5">
 {f.a}
 </AccordionContent>
 </AccordionItem>
 ))}
 </Accordion>
 <p data-testid="faq-closing" className="text-muted_text leading-relaxed mt-10 max-w-3xl text-[15px]">
 {CLOSING_COPY}
 </p>
 </section>

 <section data-testid="faq-still-have" className="container-pad max-w-7xl mx-auto pb-16">
 <div className="card-soft p-8 sm:p-10 grid lg:grid-cols-12 gap-8 items-center">
 <div className="lg:col-span-1">
 <div className="w-12 h-12 rounded-2xl bg-gold-500/15 text-gold-600 flex items-center justify-center">
 <LifeBuoy className="w-6 h-6" strokeWidth={1.5} />
 </div>
 </div>
 <div className="lg:col-span-8">
 <h3 className="font-heading text-2xl font-semibold text-ink">Still have questions?</h3>
 <p className="mt-2 text-muted_text leading-relaxed">Our care coordinators are happy to walk you through anything, from coverage to clinical fit.</p>
 </div>
 <div className="lg:col-span-3 flex lg:justify-end">
 <Link to="/contact" data-testid="faq-contact-cta" className="btn-primary">Contact our team</Link>
 </div>
 </div>
 </section>

 <CTABanner />
 </SiteLayout>
 );
}
